import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { cn } from '../../lib/utils';
import {
  Brain, Image, MessageSquare, AlertTriangle,
  Package, CheckSquare, Upload,
  Users, UserCircle, UsersRound,
  Box, Warehouse,
  BarChart3, MapPin, TrendingUp, MousePointer,
  Settings, Mail, CreditCard,
  ChevronLeft, ChevronRight, LogOut, Home,
  PanelLeftClose, PanelLeft
} from 'lucide-react';
import { Button } from '../ui/button';

// Sidebar navigation structure
const SIDEBAR_SECTIONS = [
  {
    id: 'ai-data',
    title: 'AI & Data',
    icon: Brain,
    color: 'text-purple-500',
    items: [
      { id: 'ai-learning', label: 'AI Learning', icon: Brain, tab: 'ai-learning' },
      { id: 'image-review', label: 'Image Review', icon: Image, tab: 'ai-learning', subview: 'review' },
      { id: 'feedback', label: 'Feedback Data', icon: MessageSquare, tab: 'feedback' },
      { id: 'training', label: 'AI Training', icon: AlertTriangle, tab: 'training' },
    ]
  },
  {
    id: 'support',
    title: 'Supporto',
    icon: MessageSquare,
    color: 'text-[#B08B5C]',
    items: [
      { id: 'support-requests', label: 'Richieste Supporto', icon: MessageSquare, tab: 'support' },
    ]
  },
  {
    id: 'materials',
    title: 'Materials',
    icon: Package,
    color: 'text-sr-gold',
    items: [
      { id: 'materials-lib', label: 'Materials Library', icon: Package, tab: 'materials' },
      { id: 'approvals', label: 'Approvals', icon: CheckSquare, tab: 'approvals' },
      { id: 'bulk-import', label: 'Bulk Import', icon: Upload, tab: 'bulk' },
      { id: 'samples', label: 'Samples', icon: Box, tab: 'samples' },
    ]
  },
  {
    id: 'users',
    title: 'Users',
    icon: Users,
    color: 'text-[var(--gold)]',
    items: [
      { id: 'users-list', label: 'Users', icon: Users, tab: 'users' },
      { id: 'advisors', label: 'Advisors', icon: UserCircle, tab: 'advisor' },
      { id: 'advisor-groups', label: 'Advisor Groups', icon: UsersRound, tab: 'advisor-groups' },
    ]
  },
  {
    id: 'analytics',
    title: 'Analytics',
    icon: BarChart3,
    color: 'text-green-500',
    items: [
      { id: 'stats', label: 'Statistics', icon: TrendingUp, tab: 'stats' },
      { id: 'city-analytics', label: 'City Analytics', icon: MapPin, tab: 'city' },
      { id: 'b2b', label: 'B2B Analytics', icon: BarChart3, tab: 'b2b' },
      { id: 'onboarding', label: 'Onboarding', icon: MousePointer, tab: 'onboarding' },
    ]
  },
  {
    id: 'system',
    title: 'System',
    icon: Settings,
    color: 'text-stone-500',
    items: [
      { id: 'settings', label: 'Settings', icon: Settings, tab: 'settings' },
      { id: 'email', label: 'Email Templates', icon: Mail, tab: 'email' },
      { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard, tab: 'subscriptions' },
    ]
  },
];

const AdminSidebar = ({ activeTab, onTabChange, collapsed, onToggleCollapse }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('ai-data');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className={cn(
      "fixed left-0 top-0 h-screen bg-stone-900 text-white flex flex-col transition-all duration-300 z-50",
      collapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="p-4 border-b border-stone-700 flex items-center justify-between">
        {!collapsed && (
          <div>
            <h1 className="font-semibold text-lg">Admin Console</h1>
            <p className="text-xs text-stone-400">SlabReality</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className="text-stone-400 hover:text-white hover:bg-stone-800"
        >
          {collapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {SIDEBAR_SECTIONS.map((section) => (
          <div key={section.id} className="mb-2">
            {/* Section Header */}
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors",
                expandedSection === section.id ? "text-white" : "text-stone-400 hover:text-white"
              )}
            >
              <section.icon className={cn("w-4 h-4", section.color)} />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{section.title}</span>
                  <ChevronRight className={cn(
                    "w-4 h-4 transition-transform",
                    expandedSection === section.id && "rotate-90"
                  )} />
                </>
              )}
            </button>

            {/* Section Items */}
            {(expandedSection === section.id || collapsed) && (
              <div className={cn("mt-1", !collapsed && "ml-4")}>
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => onTabChange(item.tab)}
                    data-testid={`sidebar-${item.id}`}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-sm rounded-lg transition-colors",
                      activeTab === item.tab
                        ? "bg-stone-700 text-white"
                        : "text-stone-400 hover:text-white hover:bg-stone-800"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="w-4 h-4" />
                    {!collapsed && <span>{item.label}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-stone-700 space-y-2">
        {/* Back to App */}
        <Link
          to="/dashboard"
          className={cn(
            "flex items-center gap-3 px-3 py-2 text-sm text-stone-400 hover:text-white hover:bg-stone-800 rounded-lg transition-colors",
            collapsed && "justify-center"
          )}
        >
          <Home className="w-4 h-4" />
          {!collapsed && <span>Back to App</span>}
        </Link>

        {/* User & Logout */}
        {!collapsed && user && (
          <div className="px-3 py-2 bg-stone-800 rounded-lg">
            <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-stone-400 truncate">{user.email}</p>
          </div>
        )}
        
        <button
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-stone-800 rounded-lg transition-colors",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );
};

const AdminLayout = ({ children, activeTab, onTabChange }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-stone-50">
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={onTabChange}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      
      {/* Main Content */}
      <main className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "ml-16" : "ml-64"
      )}>
        {/* Top Bar */}
        <header className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 z-40">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-stone-900">
                {SIDEBAR_SECTIONS.flatMap(s => s.items).find(i => i.tab === activeTab)?.label || 'Dashboard'}
              </h1>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export { AdminLayout, AdminSidebar, SIDEBAR_SECTIONS };
export default AdminLayout;
